import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { FilterData, IndexType } from "components/Extra"
import classNames from "classnames"
import { Link } from "react-router-dom"
import DisputesTable from "pages/vendor/Disputes/DisputesTable"
import { DisputesCards } from "pages/vendor/Disputes/DisputesCards"
import { DisputesGrid } from "pages/vendor/Disputes/DisputesGrid"
import { ChangeStatusDiputeModal } from "pages/vendor/Disputes/Modals"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FilterByDateRangeModal } from "components/Extra/FilterByDateRangeModal"
import { getDisputes, changeDisputeStatus } from "../../../store/disputes/actions"

export enum CurrentTapType {
  all = "disputes_all",
  pending = "disputes_pending",
  inProgress = "disputes_inProgress",
  solved = "disputes_solved",
  rejected = "disputes_rejected",
}

const tabs = [
  CurrentTapType.all,
  CurrentTapType.pending,
  CurrentTapType.inProgress,
  CurrentTapType.solved,
  CurrentTapType.rejected,
]

// Backend Dispute interface
export interface BackendDispute {
  id: number
  title: string
  claims: string
  statusId: number
  statusName: string
  issueTime: string
  completedOn: string | null
  issuedByAccount: {
    id: number
    username: string
    fullName: string
  }
  logs: any[]
  items: {
    orderId: number
    productId: number
    productName: string
    notes: string
  }[]
  documents: any[] | null
}

// Frontend Dispute interface (for UI)
export interface Dispute {
  title: string
  id: string
  createdDate: string
  createdBy: string
  attachments: string[]
  status:
    | CurrentTapType.pending
    | CurrentTapType.inProgress
    | CurrentTapType.solved
    | CurrentTapType.rejected
  description: string
}

// Helper function to format date to '23 jun, 2022' format
const formatDate = (dateString: string): string => {
  if (!dateString) return "__"
  const date = new Date(dateString)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month}, ${year}`
}

// Helper function to convert statusId to CurrentTapType
const statusIdToTapType = (statusId: number): CurrentTapType.pending | CurrentTapType.inProgress | CurrentTapType.solved | CurrentTapType.rejected => {
  switch (statusId) {
    case 1: // Open
      return CurrentTapType.pending
    case 2: // Responded
      return CurrentTapType.inProgress
    case 3: // Resolved
      return CurrentTapType.solved
    case 4: // Rejected
      return CurrentTapType.rejected
    case 5: // Closed
      return CurrentTapType.solved
    default:
      return CurrentTapType.pending
  }
}

// Helper function to map backend dispute to frontend format
const mapToDispute = (backend: BackendDispute): Dispute => {
  return {
    title: backend.title || "__",
    id: backend.id ? backend.id.toString() : "__",
    createdDate: formatDate(backend.issueTime),
    createdBy:
      backend.issuedByAccount?.fullName ||
      backend.issuedByAccount?.username ||
      "__",
    attachments:
      backend.documents?.map(
        (d: any) => d.name || d.fileName || "attachment"
      ) || [],
    status: statusIdToTapType(backend.statusId),
    description: backend.claims || "__",
  }
}

interface DisputesProps {
  isDashboardPage?: boolean
}

export const Disputes: React.FC<DisputesProps> = ({ isDashboardPage }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [currentTap, setCurrentTap] = useState(CurrentTapType.all)
  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [checkedDisputes, setCheckedDisputes] = useState<Dispute[]>([])
  const [filteredDisputes, setFilteredDisputes] = useState<Dispute[]>([])
  const [openChangeStatusModal, setOpenChangeStatusModal] = useState<{
    open: boolean
    view: "respond" | "rejected"
    dispute: Dispute | null
  }>({
    open: false,
    view: "respond",
    dispute: null,
  })

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const pageSize = 10

  // Get disputes from Redux store
  const { disputes: backendDisputes, loading, pagination } = useSelector((state: any) => state.Disputes)

  // Fetch disputes on mount and page change
  useEffect(() => {
    const statusFilter = currentTap === CurrentTapType.all
      ? undefined
      : getStatusIdFromTap(currentTap)

    dispatch(getDisputes({
      PageSize: pageSize,
      PageIndex: currentPage,
      StatusId: statusFilter,
    }, { push: navigate }))
  }, [dispatch, currentPage, currentTap])

  // Helper function to convert tap to status ID
  const getStatusIdFromTap = (tap: CurrentTapType): number | undefined => {
    switch (tap) {
      case CurrentTapType.pending:
        return 1
      case CurrentTapType.inProgress:
        return 2
      case CurrentTapType.solved:
        return 3
      case CurrentTapType.rejected:
        return 4
      default:
        return undefined
    }
  }

  // Map backend disputes to frontend format
  const disputes: Dispute[] = useMemo(() => {
    return backendDisputes.map(mapToDispute)
  }, [backendDisputes])

  // Filter disputes based on current tap
  useEffect(() => {
    if (currentTap === CurrentTapType.all) {
      setFilteredDisputes(disputes)
    } else {
      setFilteredDisputes(disputes.filter(d => d.status === currentTap))
    }
  }, [disputes, currentTap])

  const isAllChecked = useMemo(() => {
    return disputes.length > 0 && checkedDisputes.length === disputes.length
  }, [disputes, checkedDisputes])

  const checkedDisputeHandler = useCallback((dispute: Dispute) => {
    setCheckedDisputes(prev =>
      prev.includes(dispute)
        ? prev.filter(o => o !== dispute)
        : [...prev, dispute]
    )
  }, [])

  const toggleAllDisputes = useCallback(() => {
    setCheckedDisputes(isAllChecked ? [] : disputes)
  }, [isAllChecked, disputes])

  const changeTabHandler = useCallback(
    (tab: CurrentTapType) => {
      setCurrentTap(tab)
      setCurrentPage(0) // Reset to first page when changing tab
    },
    [setCurrentTap]
  )

  const resolveHandler = useCallback((dispute: Dispute) => {
    // Resolve dispute by changing status to Resolved (statusId: 3)
    const statusData = {
      statusId: 3,
      actionId: 3,
      notes: "Resolved",
      isInternal: true,
    }
    dispatch(changeDisputeStatus(Number(dispute.id), statusData, { push: navigate }))
    // setOpenChangeStatusModal({
    //   open: true,
    //   view: "respond",
    //   dispute,
    // })
  }, [dispatch, navigate])
  const rejectHandler = useCallback((dispute: Dispute) => {
    setOpenChangeStatusModal({
      open: true,
      view: "rejected",
      dispute,
    })
  }, [])
  const viewDetailsHandler = useCallback((dispute: Dispute) => {
    // Extract numeric ID from "ID-123" format
    const numericId = dispute.id.replace("ID-", "")
    navigate(`/disputes/${numericId}`)
  }, [navigate])
  const forwardToAdminHandler = useCallback((dispute: Dispute) => {
    // Forward dispute to admin - change status to Responded (statusId: 2)
    const statusData = {
      statusId: 2,
      actionId: 2,
      notes: "Forward to Nartawi",
      isInternal: true,
    }
    dispatch(changeDisputeStatus(Number(dispute.id), statusData, { push: navigate }))
  }, [dispatch, navigate])
  const deleteProductHandler = useCallback((dispute: Dispute) => {}, [])

  const onDateRangeChange = (startDate: string, endDate: string) => {
    console.log("Selected date range:", startDate, "to", endDate)
  }


  if (isDashboardPage) {
    return (
      <>
        <FilterData
          data={disputes}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          bearerFilteredData={setFilteredDisputes}
          showFilterBtnInsteadOfToggle
        />

        {/* Modals */}
        <ChangeStatusDiputeModal
          currentView={openChangeStatusModal.view}
          isOpen={openChangeStatusModal.open}
          dispute={openChangeStatusModal.dispute}
          toggle={() =>
            setOpenChangeStatusModal({
              open: false,
              view: "respond",
              dispute: null,
            })
          }
        />

        <DisputesTable
          disputes={filteredDisputes}
          isAllChecked={isAllChecked}
          checkedDisputes={checkedDisputes}
          toggleAllDisputes={toggleAllDisputes}
          checkedDisputeHandler={checkedDisputeHandler}
          resolveHandler={resolveHandler}
          rejectHandler={rejectHandler}
          forwardToAdminHandler={forwardToAdminHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      </>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("disputes_disputes")}
        />
        <FilterByDateRangeModal
          onDateRangeChange={onDateRangeChange}
          className="mb-3"
        />
        <DisputesCards />
        <ul className="nav nav-pills mb-3 px-2">
          {tabs.map(tab => (
            <li
              className="nav-item"
              style={{ flex: 1, textAlign: "center" }}
              key={tab}
            >
              <Link
                to="#"
                className={classNames(
                  { active: currentTap === tab },
                  "nav-link"
                )}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => changeTabHandler(tab)}
              >
                {t(tab)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <FilterData
        data={disputes}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredDisputes}
      />

      {/* Modals */}
      <ChangeStatusDiputeModal
        currentView={openChangeStatusModal.view}
        isOpen={openChangeStatusModal.open}
        dispute={openChangeStatusModal.dispute}
        toggle={() =>
          setOpenChangeStatusModal({
            open: false,
            view: "respond",
            dispute: null,
          })
        }
      />

      {activeTab == "list-ul" ? (
        <DisputesTable
          disputes={filteredDisputes}
          isAllChecked={isAllChecked}
          checkedDisputes={checkedDisputes}
          toggleAllDisputes={toggleAllDisputes}
          checkedDisputeHandler={checkedDisputeHandler}
          resolveHandler={resolveHandler}
          rejectHandler={rejectHandler}
          forwardToAdminHandler={forwardToAdminHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      ) : (
        <DisputesGrid
          disputes={filteredDisputes}
          viewDetailsHandler={viewDetailsHandler}
          resolveHandler={resolveHandler}
          rejectHandler={rejectHandler}
          forwardToAdminHandler={forwardToAdminHandler}
        />
      )}
    </div>
  )
}
